'use client';


import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const navGroups = [
  {
    label: 'Operations',
    items: [
      { label: 'Security Dashboard', href: '/security-dashboard', icon: 'ShieldCheckIcon', badge: null },
      { label: 'Event Monitoring', href: '/event-monitoring', icon: 'MagnifyingGlassIcon', badge: '1.2k' },
      { label: 'Incident Management', href: '/incident-management', icon: 'ExclamationTriangleIcon', badge: '4' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { label: 'Alert Configuration', href: '/alert-configuration', icon: 'BellAlertIcon', badge: null },
      { label: 'Network Assets', href: '/network-assets', icon: 'ServerStackIcon', badge: '2' },
    ],
  },
  {
    label: 'Reporting',
    items: [
      { label: 'Reports', href: '/reports', icon: 'DocumentChartBarIcon', badge: null },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full z-40 flex flex-col
        bg-zinc-950 border-r border-zinc-800/60
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Header */}
      <div className={`flex items-center h-14 px-3 border-b border-zinc-800/60 flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2.5">
            <AppLogo size={28} />
            <span className="font-semibold text-sm tracking-tight text-zinc-100">NetSIEM</span>
          </div>
        )}
        {collapsed && <AppLogo size={28} />}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
            aria-label="Collapse sidebar"
          >
            <Icon name="ChevronLeftIcon" size={14} />
          </button>
        )}
      </div>

      {/* Live indicator */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-xs font-medium text-emerald-400">Live Monitoring</span>
          <span className="ml-auto font-mono text-xs text-emerald-500/70">4,218 eps</span>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center mt-3 mb-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-1">
            {!collapsed && (
              <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
                {group.label}
              </p>
            )}
            {collapsed && <div className="my-2 mx-3 h-px bg-zinc-800/60" />}
            {group.items.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <div key={item.href} className="relative group px-2">
                  <Link
                    href={item.href}
                    className={`
                      flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium
                      transition-all duration-150 mb-0.5
                      ${isActive
                        ? 'bg-rose-600/15 text-rose-400 border border-rose-600/20' :'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/70'
                      }
                      ${collapsed ? 'justify-center' : ''}
                    `}
                  >
                    <Icon
                      name={item.icon as any}
                      size={16}
                      className={isActive ? 'text-rose-400' : 'text-zinc-500 group-hover:text-zinc-300'}
                    />
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className={`
                            text-[10px] font-semibold px-1.5 py-0.5 rounded-full tabular-nums
                            ${isActive ? 'bg-rose-500/20 text-rose-400' : 'bg-zinc-800 text-zinc-400'}
                          `}>
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                  {/* Collapsed tooltip */}
                  {collapsed && (
                    <div className="
                      absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5
                      bg-zinc-800 border border-zinc-700 rounded-md shadow-xl
                      text-xs text-zinc-200 font-medium whitespace-nowrap
                      opacity-0 group-hover:opacity-100 pointer-events-none
                      transition-opacity duration-150 z-50
                    ">
                      {item.label}
                      {item.badge && (
                        <span className="ml-2 text-[10px] bg-rose-500/20 text-rose-400 px-1 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={`border-t border-zinc-800/60 p-2 flex-shrink-0 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-zinc-800/70 cursor-pointer group transition-colors">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              SA
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-zinc-300 truncate">Selin Arslan</p>
              <p className="text-[10px] text-zinc-600 truncate">SOC Analyst L2</p>
            </div>
            <button
              onClick={onToggle}
              className="p-1 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-400 transition-colors"
              aria-label="Expand sidebar"
            >
              <Icon name="ChevronRightIcon" size={12} />
            </button>
          </div>
        ) : (
          <button
            onClick={onToggle}
            className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 flex items-center justify-center text-xs font-bold text-white hover:opacity-80 transition-opacity"
            title="Selin Arslan — SOC Analyst L2"
          >
            SA
          </button>
        )}
      </div>
    </aside>
  );
}